import React from "react";
import { useTranslation } from "react-i18next";
import { PMCustomSelector } from "../PMCustomSelector";
import { fetchUnits } from "../../../api/unit";

export const UnitCustomSelector = ({
  name,
  label,
  selected,
  onChange,
  disabled = false,
  setFieldValue,
  values,
  errors,
  touched,
  formik = false,
  zIndex,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);

  const handleSelect = (item) => {
    // Ensure we have the full unit object with displayTitle
    const unitObject = {
      id: item.id,
      displayTitle: item.displayTitle || item.title || item.name,
      title: item.title || item.displayTitle || item.name,
      name: item.name || item.displayTitle || item.title,
      ...item, // Include all other properties
    };

    if (onChange) {
      onChange({
        target: {
          name: name || "unit_id",
          value: item.id,
          item: unitObject,
          object: unitObject,
        },
        item: unitObject,
        value: item.id,
      });
    }
    if (setFieldValue && name) {
      setFieldValue(name, item.id);
    }
  };

  const fetchUnitsFunction = React.useCallback(async () => {
    try {
      const response = await fetchUnits({
        query: "",
        page: 1,
        per_page: 100,
        sortBy: "created_at",
        sort: "desc",
      });
      // Handle different response formats
      let units = [];
      if (Array.isArray(response)) {
        units = response;
      } else if (response?.data) {
        if (Array.isArray(response.data)) {
          units = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          units = response.data.data;
        }
      }
      
      // Ensure all units have displayTitle
      return units.map(unit => ({
        ...unit,
        displayTitle: unit.displayTitle || unit.title || unit.name || `Unit ${unit.id}`,
        name: unit.displayTitle || unit.title || unit.name || `Unit ${unit.id}`,
        title: unit.title || unit.displayTitle || unit.name || `Unit ${unit.id}`,
      }));
    } catch (error) {
      console.error("Error fetching units:", error);
      return [];
    }
  }, []); // Empty dependency array - function is stable

  // Convert selected unit object to format expected by PMCustomSelector
  // Use useMemo to prevent creating new object on every render
  const selectedItem = React.useMemo(() => {
    if (!selected?.id) return null;
    return {
      id: selected.id,
      name: selected.displayTitle || selected.title || selected.name,
      title: selected.displayTitle || selected.title || selected.name,
      displayTitle: selected.displayTitle || selected.title || selected.name,
      ...selected, // Include all properties from selected
    };
  }, [selected?.id, selected?.displayTitle, selected?.title, selected?.name]);

  if (formik && name && label) {
    // Use as form field
    return (
      <PMCustomSelector
        name={name}
        label={label}
        fetchFunction={fetchUnitsFunction}
        onSelect={handleSelect}
        selected={selectedItem}
        setFieldValue={setFieldValue}
        values={values}
        errors={errors}
        touched={touched}
        field="displayTitle"
        field2="title"
        title={label}
        description="Select a unit"
        showSearch={true}
        disabled={disabled}
      />
    );
  }

  // Use as standalone selector with a button/field to open dialog
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {t(label || "Unit")}
      </label>
      <div
        className={`cursor-pointer block w-full border border-gray-300 rounded-md bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm p-2 ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:border-gray-400"
        }`}
        onClick={() => !disabled && setIsOpen(true)}
      >
        {selectedItem ? (
          <div className="flex items-center justify-between">
            <span className="text-gray-900">
              {selectedItem.displayTitle || selectedItem.title || selectedItem.name}
            </span>
            {!disabled && (
              <span className="text-blue-600 text-sm">Change</span>
            )}
          </div>
        ) : (
          <div className="text-gray-400">
            {t("Click to select")} {label?.toLowerCase() || "unit"}
          </div>
        )}
      </div>
      {isOpen && (
        <PMCustomSelector
          open={isOpen}
          onClose={() => setIsOpen(false)}
          fetchFunction={fetchUnitsFunction}
          onSelect={(item) => {
            // Call handleSelect which will trigger onChange
            handleSelect(item);
            setIsOpen(false);
          }}
          selected={selectedItem}
          field="displayTitle"
          field2="title"
          title={label || "Select Unit"}
          description="Choose a unit"
          showSearch={true}
          disabled={disabled}
          zIndex={zIndex}
        />
      )}
    </div>
  );
};

